/** @jsx jsx */
import { jsx } from "theme-ui"
import React from "react"
import { graphql, Link } from "gatsby"
import PropertyTeaser from "../components/entity/property/propertyTeaser"
import Layout from "../layout"
import Container from "../components/container"
import Seo from "../components/seo"
import Hero from "../layout/Hero"

// Shape a build-time `allProperty` (MLS) node for PropertyTeaser. This mirrors
// what src/api/properties.js does for runtime rows and what the search-page
// teaserShape helpers do — same target shape (address / image / acreage /
// slug / displayStatus).
function shapeMlsNode(node) {
  const address =
    node.address ||
    [node.street_number, node.street_name].filter(Boolean).join(" ") ||
    ""
  const slug =
    node.originating_system_slug && node.mls_number
      ? `${node.originating_system_slug}/${node.mls_number}`
      : null
  return {
    ...node,
    address,
    image: node.primary_photo_url || null,
    acreage: node.lot_size_acres,
    slug,
    displayStatus: node.status,
    source: "mls",
  }
}

const GRID_LI_SX = {
  boxSizing: "border-box",
  zIndex: "1",
  position: "relative",
  backgroundColor: "white",
  marginBottom: "40px",
  width: ["100%", "calc(100% / 2 - 12.5px)", "calc(100% / 3 - 16.7px)"],
  marginRight: ["0px", "25px", "25px"],
  "&:nth-of-type(3n)": {
    marginRight: ["0px", "25px", "0px !important"],
  },
  "&:nth-of-type(2n)": {
    marginRight: ["0px", "0px", "25px"],
  },
}

const GRID_UL_SX = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  listStyle: "none",
  margin: "0",
  padding: "20px 0px",
}

class PropertyListCounty extends React.Component {
  render() {
    let metaTitle = ""
    let metaDescription = ""
    if (this.props.data.county) {
      metaTitle = this.props.data.county.metaTitle
        ? this.props.data.county.metaTitle
        : this.props.data.county.countyName
      if (this.props.data.county.metaDescription) {
        metaDescription = this.props.data.county.metaDescription
      }
    } else {
      metaTitle = this.props.pageContext.countyName + " County"
    }

    const ourProperties = this.props.data.ourproperty.nodes
    const mlsProperties = this.props.data.additionalproperties.nodes

    return (
      <Layout>
        <Seo title={metaTitle} description={metaDescription} />
        <Hero node={this.props.data.pageCounties} />
        <div className="county-outer">
          <div className="county-outer-inner">
            <Container>
              <div sx={{ padding: "20px 0px", color: "grayBlk" }}>
                <h1 sx={{ fontFamily: "Times, sans-serif" }}>{metaTitle}</h1>
              </div>

              {ourProperties[0] && (
                <ul sx={GRID_UL_SX}>
                  {ourProperties.map((node, index) => (
                    <li sx={GRID_LI_SX} key={`sanity-${node.id || index}`}>
                      <PropertyTeaser property={node} asModal={false} />
                    </li>
                  ))}
                </ul>
              )}

              {mlsProperties[0] && (
                <ul sx={GRID_UL_SX}>
                  {mlsProperties.map((node, index) => (
                    <li
                      sx={GRID_LI_SX}
                      key={`mls-${node.mls_number || index}`}
                    >
                      <PropertyTeaser
                        property={shapeMlsNode(node)}
                        asModal={false}
                      />
                    </li>
                  ))}
                </ul>
              )}

              {!ourProperties[0] && !mlsProperties[0] && (
                <div
                  sx={{
                    paddingBottom: "40px",
                    a: { textDecoration: "underline" },
                  }}
                >
                  No listings in this county right now. Please{" "}
                  <Link to="/">visit our home page</Link>, or{" "}
                  <Link to="/contact">reach out to us</Link> to chat.
                </div>
              )}
            </Container>
          </div>
        </div>
      </Layout>
    )
  }
}
export default PropertyListCounty

export const postQuery = graphql`
  query PropertyListByCounty($countyName: String!) {
    county: sanityCounty(countyName: { eq: $countyName }) {
      id
      countyName
      metaTitle
      metaDescription
    }
    pageCounties: sanityPageDefinition(slug: { current: { eq: "counties" } }) {
      _rawHero(resolveReferences: { maxDepth: 10 })
    }
    ourproperty: allSanityProperty(
      filter: {
        ourcounty: { eq: $countyName }
        status: { ne: "sold" }
        _id: { regex: "/^((?!drafts).)*$/" }
      }
      sort: { order: DESC, fields: price }
    ) {
      nodes {
        id
        displayStatus: status
        county: ourcounty
        price
        acreage
        address: propertyName
        description: propertySummary
        slug {
          current
        }
        sanityimage: propertyFeaturedImage {
          asset {
            gatsbyImageData(
              width: 1000
              height: 650
              placeholder: DOMINANT_COLOR
              formats: [AUTO, WEBP, AVIF]
              layout: CONSTRAINED
            )
          }
        }
      }
    }
    additionalproperties: allProperty(
      filter: { county: { eq: $countyName }, mls_number: { ne: null } }
      sort: { order: DESC, fields: price }
    ) {
      nodes {
        mls_number
        originating_system_slug
        price
        lot_size_acres
        primary_photo_url
        address
        street_number
        street_name
        county
        status
        description
      }
    }
  }
`
